import { startCase } from 'lodash';
import { Conversation } from '@twilio/conversations';

import { ConversationStateType } from 'hooks/conversation/types';
import {
  DisplayTwilioParticipantType,
  FormattedTwilioParticipantsType,
} from 'services/twilio/types';
import { PARTICIPANT_ADVOCATE_IMAGE_MAP, PARTICIPANT_PREFIX } from 'constants/twilio';
import { defaultSystemParticipant } from './constants';

/**
 * Iterate through conversation Participants and mutate data to fit context for ChatUI.
 * Sebastian is always available by default and inserted via hard-code.
 *
 * There exists a discreet "gotcha"/bug where Twilio's participantJoined/participantLeft hooks
 * don't trigger when advocate assignment is toggled within Front App. To supplement this, hook
 * messageAdded is also used to check whether author is already in list of participants. If not, then append.
 *
 * @param twilioParticipants Raw response of Participants from Twilio OR a new participant to append.
 * @param existingParticipants Existing list of Participants to append a new participant to.
 * @returns { advocates, customer, system } Formatted Participants
 */
export const formatParticipants = (
  twilioParticipants: Array<{ identity: string | null }>,
  existingParticipants?: FormattedTwilioParticipantsType,
) => {
  const advocates: Array<DisplayTwilioParticipantType> = existingParticipants
    ? existingParticipants.advocates
    : [];
  const customer: Array<DisplayTwilioParticipantType> = existingParticipants
    ? existingParticipants.customer
    : [];
  const system: Array<DisplayTwilioParticipantType> = existingParticipants
    ? existingParticipants.system
    : [defaultSystemParticipant];

  twilioParticipants.forEach((twilioParticipant) => {
    if (twilioParticipant.identity) {
      const identityFragments = twilioParticipant.identity.split(':');
      const [identityType, identityName] = [identityFragments[0], identityFragments[1]];

      if (identityType === PARTICIPANT_PREFIX.CUSTOMER) {
        customer.push({
          name: identityName,
          identity: twilioParticipant.identity,
          imageUrl: null,
        });
      } else if (identityType === PARTICIPANT_PREFIX.ADVOCATE) {
        const imageUrl = PARTICIPANT_ADVOCATE_IMAGE_MAP.hasOwnProperty(identityName)
          ? PARTICIPANT_ADVOCATE_IMAGE_MAP[identityName]
          : PARTICIPANT_ADVOCATE_IMAGE_MAP.DEFAULT;
        advocates.push({
          name: startCase(identityName),
          identity: twilioParticipant.identity,
          imageUrl,
        });
      }
    }
  });

  return { advocates, customer, system };
};

/**
 * Determine if message has been seen by the user.
 * @param messageIndex The index of the message to check.
 * @param c The index of the last read message; The `conversationState`; the Twilio Conversation
 * @returns True if the `messageIndex` is behind the Read Horizon. Otherwise false.
 * @example
 * isMessageRead(4, 10)
 * @example
 * const {conversationState} = useConversation();
 * isMessageRead(twilioMessageIndex, conversationState)
 * isMessageRead(twilioMessageIndex, conversationState.twilio.conversation)
 */
export const isMessageRead = (
  messageIndex: number,
  c: number | ConversationStateType | Conversation,
) => {
  if (typeof c === 'number') return messageIndex <= c;
  if (typeof c === 'object') {
    if (c instanceof Conversation) {
      return messageIndex <= (c.lastReadMessageIndex ?? 0);
    }

    return messageIndex <= (c.twilio?.conversation.lastReadMessageIndex ?? 0);
  }
  return false;
};

/**
 * Determine if the `messageIndex` is the last message.
 * @param messageIndex
 * @param totalMessages
 * @returns True of `messageIndex` is the last message. Otherwise, False.
 */
export const isLastMessage = (messageIndex: number, totalMessages: number) => {
  return messageIndex === totalMessages - 1;
};
